// Brand registry + resolver — single source of truth o brandech.
//
// Cíl: jeden build z mainV5, branding se vybere ZA BĚHU podle domény
// (window.location.hostname). Přidání nového brandu = jeden záznam v BRANDS
// + assety + jeden [data-brand="…"] blok v SCSS (barvy/písmo).
//
// Vzor resolveru je záměrně stejný jako `domainLanguageDetector` v `src/i18n.js`
// (jazyk podle TLD) — tady vybíráme brand podle celého hostname.
//
// POZN. rozšiřitelnost: Brand je rozdělený na sekce (identity/assets/meta), ať
// přidání dalšího pole je jen jeden řádek a nerozbije existující brandy.

import featuresConfig from "../../features-config.json";

// statotest = default brand → assety zůstávají na konvenčních místech (logo sdílí
// i ext-charts header / Page500, favicon v public rootu potřebuje index.html před JS).
// Každý PŘIDANÝ brand má samostatnou složku src/assets/brands/<brand>/ + public/brands/<brand>/.
import statotestLogo from "../assets/img/statotest-logo-white-red.svg";
import loginCoverDefault from "../assets/img/statotest-blueprint.png";
import smartTimberLogo from "../assets/brands/smart-timber/logo.png";

/** Klíč do features-config.json (route/layout profil brandu): 'main' | 're-branded' | 'r-ext-charts'. */
export type FeatureKey = keyof typeof featuresConfig;

/** SEO / PWA / mobile-chrome metadata brandu. */
export interface BrandMeta {
  description: string;
  keywords: string;
  author: string;
  /** <meta name="theme-color"> + PWA manifest theme_color (barva chrome na mobilu). */
  themeColor: string;
  /** Cesta do /public na PWA manifest tohoto brandu. */
  manifest: string;
}

export interface Brand {
  /** Stabilní id — používá se jako `data-brand` atribut a v SCSS selektorech. */
  id: string;
  /** Přesné hostnames, které patří tomuto brandu. */
  hostnames: string[];
  /** Profil ve features-config.json — určuje, které routy/layout brand vidí. */
  featureKey: FeatureKey;

  // ── Identita (texty) ──
  /** document.title + alt loga. */
  title: string;
  /** Právní/organizační název (footer copyright, reporty…). */
  organisationName: string;
  /** Footer: "<portalName> Portal". */
  portalName: string;
  /** Web brandu — footer odkaz (prázdný = bez odkazu). */
  website: string;
  /** Odkaz na dokumentaci — footer odkaz (prázdný = bez odkazu; brand bez vlastní dokumentace). */
  docsUrl: string;

  // ── Assety ──
  /** Logo (sidebar + login) — importovaný bundlovaný asset. */
  logo: string;
  /** Favicon — cesta do /public (přepíná se za běhu v bootstrapu). */
  favicon: string;
  /** Login pozadí motiv (blueprint) — importovaný asset. */
  loginCover: string;

  // ── Meta ──
  meta: BrandMeta;

  // Barvy + písmo se řeší přes [data-brand] v SCSS (_brand-runtime.scss), ne tady.
}

export const BRANDS: Brand[] = [
  {
    id: "statotest",
    hostnames: ["portal.statotest.com", "testportal.statotest.com", "localhost"],
    featureKey: "main",
    title: "Statotest",
    organisationName: "Statotest s.r.o.",
    portalName: "Statotest",
    website: "", // prázdné → footer zůstane prostý text (bez odkazu), jako původně. Budoucí brand smí vyplnit.
    docsUrl: "https://docs.statotest.com/",
    logo: statotestLogo,
    favicon: "/favicon.ico",
    loginCover: loginCoverDefault,
    meta: {
      description: "Statotest - Smart IOT monitoring of bridges, buildings and many more",
      keywords: "Statotest,Monitoring,Buildings,Constructions,Static Security,Dynamic Monitoring",
      author: "Lukáš Kramár",
      themeColor: "#1F04A3",
      manifest: "/manifest.json",
    },
  },
  {
    id: "smart-timber",
    hostnames: ["smarttimberbridges.statotest.com"],
    featureKey: "re-branded",
    title: "Smart Timber Bridges",
    organisationName: "Smart Timber Bridges",
    portalName: "Smart Timber Bridges",
    website: "",
    docsUrl: "https://docs.statotest.com/", // sdílí dokumentaci s hlavním portálem
    logo: smartTimberLogo,
    favicon: "/brands/smart-timber/favicon.ico",
    loginCover: loginCoverDefault,
    meta: {
      description: "Smart Timber Bridges - Smart IOT monitoring of bridges, buildings and many more",
      keywords: "Smart Timber Bridges,Monitoring,Static Security,Dynamic Monitoring",
      author: "Statotest s.r.o.",
      themeColor: "#7bab3d",
      manifest: "/brands/smart-timber/manifest.json",
    },
  },
];

/** Fallback brand, když hostname nikam nesedí (dev, neznámá doména). */
export const DEFAULT_BRAND: Brand =
  BRANDS.find((b) => b.id === "statotest") ?? BRANDS[0];

/**
 * Dev override pro testování brandů na localhostu:
 *   - `?brand=smart-timber` v URL (uloží se do localStorage, ať přežije reload)
 *   - `localStorage.setItem('brand', 'smart-timber')`
 * Záměrně jen ve vývoji (import.meta.env.DEV) — v produkci se brand vybírá
 * výhradně podle domény, aby nešlo cizí brand „zaseknout" přes sdílený odkaz.
 */
function getBrandOverrideId(): string | null {
  if (!import.meta.env.DEV) { return null; }
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("brand");
    if (fromQuery) {
      window.localStorage.setItem("brand", fromQuery);
      return fromQuery;
    }
    return window.localStorage.getItem("brand");
  } catch {
    return null;
  }
}

/**
 * Vrátí brand pro aktuální běh. Pořadí:
 *   1) dev override (?brand= / localStorage) — jen pokud id existuje v BRANDS
 *   2) přesná shoda window.location.hostname s Brand.hostnames
 *   3) DEFAULT_BRAND
 *
 * hostname je dostupný synchronně při načtení modulu → lze volat i mimo React.
 */
export function getBrand(): Brand {
  const overrideId = getBrandOverrideId();
  if (overrideId) {
    const overridden = BRANDS.find((b) => b.id === overrideId);
    if (overridden) {
      return overridden;
    }
  }

  const hostname = window.location.hostname;
  const matched = BRANDS.find((b) => b.hostnames.includes(hostname));
  return matched ?? DEFAULT_BRAND;
}
